import { useEffect, useState } from "react";
import { useRouteError } from "react-router-dom";

// A deploy deletes the previous build's page files. A tab opened before the
// deploy still asks for them when it navigates, and the page failed to load:
// "Book a demo" showed React Router's bare error screen until a manual reload
// (BUG-203). Reloading fetches the new build at the same address. The 10 s
// window stops a loop when the file is missing for any other reason.
const RELOAD_KEY = "aiia-stale-build-reload-at";
const STALE_BUILD =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isStaleBuildError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return STALE_BUILD.test(message);
}

// Reloads unless this tab already reloaded for the same reason in the last 10 s.
export function reloadForNewBuild() {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) || 0) < 10_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reload anyway; the browser stops a tight loop itself.
  }
  window.location.reload();
  return true;
}

// Root errorElement: reload for a stale build, otherwise a plain message.
export function RouteError() {
  const error = useRouteError();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    console.error(error);
    if (!(isStaleBuildError(error) && reloadForNewBuild())) setFailed(true);
  }, [error]);

  if (!failed) return null;
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 24,
        textAlign: "center",
        background: "#f7f6f2",
        color: "#1c3038",
        fontFamily: "Inter, Arial, sans-serif",
      }}
    >
      <p style={{ margin: 0, fontSize: 18 }}>This page didn't load.</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: "10px 20px", border: 0, borderRadius: 6, background: "#1c3038", color: "#fff", fontSize: 14, cursor: "pointer" }}
      >
        Reload
      </button>
    </div>
  );
}
